import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Campaigns from './marketing/Campaigns';
import {
  MARKETING_NAV_PAGES,
  type MarketingPageId,
} from './marketing/MarketingHomeDashboard';

const marketingPages = MARKETING_NAV_PAGES;

function EmailCampaignsContent({
  onOpenPage,
  pages,
}: {
  onOpenPage: (pageId: MarketingPageId) => void;
  pages: typeof MARKETING_NAV_PAGES;
}) {
  return (
    <Campaigns
      onOpenMarketingPage={onOpenPage}
      marketingNavPages={pages}
    />
  );
}

export default function Marketing() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024,
  );

  const availablePages = React.useMemo(() => marketingPages, []);

  const [activePage, setActivePage] = useState<MarketingPageId>(() => {
    try {
      const page = new URLSearchParams(window.location.search).get('page');
      if (page && availablePages.some((p) => p.id === page)) {
        return page as MarketingPageId;
      }
    } catch {
      /* ignore */
    }
    return 'email-campaigns';
  });

  const isHome = activePage === 'email-campaigns';

  const goHome = useCallback(() => {
    setActivePage('email-campaigns');
    navigate('/marketing', { replace: true });
  }, [navigate]);

  const openPage = useCallback(
    (pageId: MarketingPageId) => {
      setActivePage(pageId);
      if (pageId === 'email-campaigns') {
        navigate('/marketing', { replace: true });
      } else {
        navigate(`/marketing?page=${encodeURIComponent(pageId)}`, { replace: true });
      }
    },
    [navigate],
  );

  // Honor ?page= deep links; map legacy pages into Email & SMS tabs
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    if (page === 'discount-codes') {
      navigate('/marketing?tab=discounts', { replace: true });
      return;
    }
    if (page === 'email-templates') {
      navigate('/marketing?tab=templates', { replace: true });
      return;
    }
    if (page === 'referral-program') {
      navigate('/marketing?tab=referral', { replace: true });
      return;
    }
    if (page === 'media-manager') {
      params.delete('page');
      const search = params.toString();
      navigate(search ? `/marketing?${search}` : '/marketing', { replace: true });
      return;
    }
    if (page === 'lead-forms') {
      navigate('/crm/lead-forms', { replace: true });
      return;
    }
    if (!page) {
      setActivePage('email-campaigns');
      return;
    }
    if (!availablePages.some((p) => p.id === page)) {
      setActivePage('email-campaigns');
      navigate('/marketing', { replace: true });
      return;
    }
    setActivePage(page as MarketingPageId);
  }, [location.search, availablePages, navigate]);

  React.useEffect(() => {
    if (activePage === 'email-campaigns') return;
    if (!availablePages.some((page) => page.id === activePage)) {
      goHome();
    }
  }, [availablePages, activePage, goHome]);

  const lastWidthRef = React.useRef(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );
  React.useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      if (currentWidth === lastWidthRef.current) return;
      lastWidthRef.current = currentWidth;
      setIsMobile(currentWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    localStorage.setItem('sales_map_open', 'false');
    window.dispatchEvent(new CustomEvent('salesMapChanged'));
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const appContent = document.querySelector('[data-app-content]');
    const scrollY = window.scrollY || window.pageYOffset || 0;

    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
    };

    body.classList.add('marketing-page-active');
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.height = '100%';
    root && (root.style.overflow = 'hidden');
    root && (root.style.height = '100%');
    appContent instanceof HTMLElement && (appContent.style.overflow = 'hidden');
    appContent instanceof HTMLElement && (appContent.style.height = '100%');
    appContent instanceof HTMLElement && (appContent.style.maxHeight = '100%');
    window.scrollTo(0, 0);

    return () => {
      body.classList.remove('marketing-page-active');
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.overflow = '';
        root.style.height = '';
      }
      if (appContent instanceof HTMLElement) {
        appContent.style.overflow = '';
        appContent.style.height = '';
        appContent.style.maxHeight = '';
      }
      window.scrollTo(0, scrollY);
    };
  }, []);

  const currentPage =
    availablePages.find((page) => page.id === activePage) ||
    availablePages[0] ||
    marketingPages[0];

  const showSectionChrome = !isHome;

  return (
    <div
      className="flex flex-col h-full min-h-0 bg-[#f1f2f4] relative overflow-hidden"
      data-marketing-page
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div className="flex-1 flex flex-col overflow-hidden min-h-0 min-w-0">
        {showSectionChrome && (
          <div
            className={`shrink-0 bg-white border-b border-gray-200/60 flex items-center ${
              isMobile ? 'px-3 py-2' : 'px-4 py-2.5'
            }`}
          >
            <button
              type="button"
              onClick={goHome}
              className={`rounded-[10px] active:bg-gray-100 hover:bg-gray-50 transition-colors touch-manipulation flex items-center gap-1.5 ${
                isMobile ? 'p-1.5 mr-2' : 'px-2 py-1.5 mr-2'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Back to Email & SMS Campaigns"
            >
              <ArrowLeft className="h-4 w-4 text-gray-600" />
              {!isMobile && (
                <span className="text-[13px] font-medium text-gray-600">Email & SMS</span>
              )}
            </button>
            <div className="flex items-center space-x-2.5 flex-1 min-w-0">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-gray-50">
                {React.createElement(currentPage.icon, {
                  className: 'h-4 w-4 text-gray-800',
                })}
              </div>
              <div className="flex-1 min-w-0">
                <h1
                  className={`font-semibold text-gray-900 truncate ${isMobile ? 'text-base' : 'text-[15px]'}`}
                  style={{ letterSpacing: '-0.01em' }}
                >
                  {currentPage.label}
                </h1>
                {!isMobile && (
                  <p className="text-[12px] text-gray-500 truncate">{currentPage.description}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="h-full min-h-0 overflow-hidden">
            <EmailCampaignsContent onOpenPage={openPage} pages={availablePages} />
          </div>
        </div>
      </div>
    </div>
  );
}
